import { formatCurrency } from '@/libs/antd/formater';
import {
	getNAPAnalysis,
	getNAPAuthorityMember,
	getNAPDetail,
	getNAPEDDPEPAnalysis,
	getNAPInternalComplianceDetail,
	getNAPRecommendationDetail,
	getNAPRiskMitigationDetail,
	getNAPSummaryConsent,
	getNAPWholesaleDetail,
} from '@/libs/fleet-api/services/nap';
import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { FC, Fragment, ReactNode, useEffect, useMemo } from 'react';

const NAPDownloadModalContentJF: FC<{
	prospectId: string;
	onClose: () => void;
}> = ({ prospectId, onClose }) => {
	const getNAPDetailQuery = useQuery({
		queryKey: ['getNAPDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPDetail(id);
		},
	});

	const getWholesaleDetailQuery = useQuery({
		queryKey: ['getNAPWholesaleDetail', prospectId] as const,
		queryFn: async ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			const data = await getNAPWholesaleDetail(id);
			data.data.restruGpStatusOptions =
				data.data.restruGpStatusOptions.reverse();

			data.data.restruGpCovidStatusOptions =
				data.data.restruGpCovidStatusOptions.reverse();

			return data;
		},
	});

	const getNAPAuthorityMemberQuery = useQuery({
		queryKey: [
			'getNAPAuthorityMember',
			getWholesaleDetailQuery.data?.data?.authorityLevel?.id,
		] as const,
		queryFn: async ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			const res = await getNAPAuthorityMember({
				authorityLevelId: String(id),
				prospectId,
			});
			return res;
		},
		enabled: !!getWholesaleDetailQuery.data?.data?.authorityLevel?.id,
	});

	const getNAPSummaryConsentQuery = useQuery({
		queryKey: ['getNAPSummaryConsent', prospectId],
		queryFn: () => getNAPSummaryConsent(prospectId),
	});

	const getNAPEDDPEPAnalysisQuery = useQuery({
		queryKey: ['getNAPEDDPEPAnalysis', prospectId],
		queryFn: () => getNAPEDDPEPAnalysis(prospectId),
	});

	const getNAPRiskMitigationQuery = useQuery({
		queryKey: ['getNAPRiskMitigationDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPRiskMitigationDetail(id);
		},
	});

	const getNAPRecommendationQuery = useQuery({
		queryKey: ['getNAPRecommendationDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPRecommendationDetail(id);
		},
	});

	const getNAPInternalComplianceQuery = useQuery({
		queryKey: ['getNAPInternalComplianceDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPInternalComplianceDetail(id);
		},
	});

	const getNAPAnalysisQuery = useQuery({
		queryKey: ['getNAPAnalysis', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPAnalysis(id);
		},
	});

	const { data: complianceData } = getNAPInternalComplianceQuery?.data ?? {};
	const { data: analysisData } = getNAPAnalysisQuery?.data ?? {};

	const loaded = useMemo(() => {
		return Boolean(
			getNAPDetailQuery.data &&
				getWholesaleDetailQuery.data &&
				getNAPSummaryConsentQuery.data &&
				getNAPEDDPEPAnalysisQuery.data &&
				getNAPRiskMitigationQuery.data &&
				getNAPRecommendationQuery.data &&
				getNAPInternalComplianceQuery.data &&
				getNAPAnalysisQuery.data,
		);
	}, [
		getNAPDetailQuery.data,
		getWholesaleDetailQuery.data,
		getNAPSummaryConsentQuery.data,
		getNAPEDDPEPAnalysisQuery.data,
		getNAPRiskMitigationQuery.data,
		getNAPRecommendationQuery.data,
		getNAPInternalComplianceQuery.data,
		getNAPAnalysisQuery.data,
	]);

	useEffect(() => {
		// if (Date.now()) return;
		if (!loaded) return;
		const container = document.getElementById('download-nap');
		if (!container) return;

		const printDoc = new jsPDF({
			orientation: 'portrait',
			unit: 'px',
			format: [container.clientHeight, container.clientWidth],
			compress: true,
		});

		setTimeout(() => {
			printDoc.html(container, {
				x: 0,
				y: 0,
				fontFaces: [
					{
						family: 'Balsamiq-Sans',
						weight: 'normal',
						stretch: 'normal',
						src: [
							{
								url: 'http://fonts.gstatic.com/s/balsamiqsans/v3/P5sEzZiAbNrN8SB3lQQX7Pnc8dkdIYdNHzs.ttf',
								format: 'truetype',
							},
						],
					},
				],
				image: {
					type: 'jpeg',
					quality: 0.5,
				},
				html2canvas: {
					logging: false,
					letterRendering: false,
					allowTaint: false,
					useCORS: true,
					scale: 1,
					width: container.clientWidth,
					height: container.clientHeight,
				},
				callback(doc) {
					doc.save(`NAP-JF-${Date.now().toString()}`);
					if (onClose) {
						onClose();
					}
				},
			});
		}, 1000);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loaded]);

	if (!loaded) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const renderField = (no: string, title: string, children: ReactNode) => {
		return (
			<Row wrap={false}>
				<Col
					flex="none"
					style={{
						width: 80,
						textAlign: 'center',
						fontWeight: 'bold',
					}}
				>
					{no}
				</Col>
				<Col flex="auto">
					<strong>{title}</strong>
					<div>{children}</div>
				</Col>
			</Row>
		);
	};

	const header = () => {
		const { napNo } = getNAPDetailQuery?.data?.data ?? {};
		const { subject, subjectOptions } =
			getWholesaleDetailQuery.data?.data ?? {};

		return (
			<div>
				<h1
					style={{
						textAlign: 'center',
						background: '#d0cece',
						fontSize: 30,
					}}
				>
					Nota Analisa Pembiayaan JF Commercial
				</h1>
				<div style={{ marginBottom: 20 }}>
					<Row>
						<Col
							flex="none"
							style={{ width: 200 }}
						>
							Nomor
						</Col>
						<Col
							flex="none"
							style={{ width: 20 }}
						>
							<center>:</center>
						</Col>
						<Col flex="auto">{napNo}</Col>
					</Row>
					<Row>
						<Col
							flex="none"
							style={{ width: 200 }}
						>
							Tanggal
						</Col>
						<Col
							flex="none"
							style={{ width: 20 }}
						>
							<center>:</center>
						</Col>
						<Col flex="auto">
							{dayjs().utc(true).local().format('DD MMMM YYYY')}
						</Col>
					</Row>
					<Row>
						<Col
							flex="none"
							style={{ width: 200 }}
						>
							Kepada
						</Col>
						<Col
							flex="none"
							style={{ width: 20 }}
						>
							<center>:</center>
						</Col>
						<Col flex="auto" />
					</Row>
					<Row>
						<Col
							flex="none"
							style={{ width: 200 }}
						>
							Dari
						</Col>
						<Col
							flex="none"
							style={{ width: 20 }}
						>
							<center>:</center>
						</Col>
						<Col flex="auto" />
					</Row>
				</div>

				<table className="cell-border">
					<tbody>
						{subjectOptions?.map((record, i) => (
							<tr key={i}>
								<td style={{ width: 1 }}>
									{record.id === subject?.id ? `[x]` : `[ ]`}
								</td>
								<td>{record.value}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	};
	const generalInfoFragment = () => {
		const { data } = getWholesaleDetailQuery.data ?? {};
		const proposers = getNAPAuthorityMemberQuery.data?.data?.proposers ?? [];
		const committees = getNAPAuthorityMemberQuery.data?.data?.committees ?? [];

		return (
			<table className="cell-border">
				<thead>
					<tr>
						<th
							className="bg"
							colSpan={7}
						>
							INFORMASI UMUM
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td style={{ width: 300 }}>
							<strong>Nama Customer</strong>
						</td>
						<td colSpan={3}>{data?.applicantName}</td>
						<td>Tahun berdiri</td>
						<td colSpan={2}>{data?.establishmentYear}</td>
					</tr>
					<tr>
						<td>
							<strong>Jenis Industri</strong>
						</td>
						<td colSpan={3}>{data?.industrySector}</td>
						<td>Tahun beroperasi</td>
						<td colSpan={2}>{data?.operationalYear}</td>
					</tr>
					<tr>
						<td>
							<strong>Nama Group Usaha</strong>
						</td>
						<td colSpan={6}>{data?.businessGroupName}</td>
					</tr>
					<tr>
						<td>
							<strong>Pemberi Referral BMRI</strong>
						</td>
						<td colSpan={6}>{data?.bmriReferralProviderName}</td>
					</tr>
					<tr>
						<td>
							<strong>No. Consent Approval</strong>
						</td>
						<td colSpan={6}>{data?.consentApprovalNumber}</td>
					</tr>
					<tr>
						<td>
							<strong>Tanggal Consent Approval</strong>
						</td>
						<td colSpan={6}>
							{data?.consentApprovalDate &&
								dayjs(data.consentApprovalDate).format('DD/MM/YYYY')}
						</td>
					</tr>
					<tr>
						<td rowSpan={2}>
							<strong>Status Restru/GP di MTF</strong>
						</td>
						<td>{`${data?.restruGpStatus?.id === 1 ? '[x]' : '[ ]'} Ya`}</td>
						<td colSpan={2}>
							{data?.restruGpCovidStatusOptions?.map((status) => {
								return (
									<div
										key={status.id}
									>{`${data?.restruGpCovidStatus?.id === status.id ? '[x]' : '[ ]'} ${status.value}`}</div>
								);
							})}
						</td>
						<td colSpan={3}>
							JT Restru/GP
							<div>
								{data?.restruGpDueDate &&
									dayjs(data.restruGpDueDate).format('DD/MM/YYYY')}
							</div>
						</td>
					</tr>
					<tr>
						<td colSpan={6}>
							{`${data?.restruGpStatus?.id === 1 ? '[ ]' : '[x]'} Tidak`}
						</td>
					</tr>
					<tr>
						<td>
							<strong>Kewenangan Memutus</strong>
						</td>
						<td colSpan={6}>{data?.authorityLevel?.value}</td>
					</tr>
					<tr>
						<td>
							<strong>Berdasarkan Nota/Ketentuan</strong>
						</td>
						<td colSpan={6}>{data?.regulationReference}</td>
					</tr>
					<tr>
						<td rowSpan={proposers.length ? proposers.length + 1 : 3}>
							<strong>Pengusul</strong>
						</td>
						<th
							colSpan={3}
							className="bg"
						>
							Nama
						</th>
						<th
							colSpan={3}
							className="bg"
						>
							Jabatan
						</th>
					</tr>
					{proposers.length ? (
						proposers.map((record, i) => (
							<tr key={i}>
								<td colSpan={3}>
									{i + 1}. {record.name}
								</td>
								<td colSpan={3}>
									{i + 1}. {record.role}
								</td>
							</tr>
						))
					) : (
						<Fragment>
							<tr>
								<td colSpan={3}>1.</td>
								<td colSpan={3}>1.</td>
							</tr>
							<tr>
								<td colSpan={3}>2.</td>
								<td colSpan={3}>2.</td>
							</tr>
						</Fragment>
					)}
					{committees.length ? (
						committees.map((record, i) => (
							<tr>
								{i === 0 && (
									<td rowSpan={committees.length}>
										<strong>Recommending Committe</strong>
									</td>
								)}
								<td colSpan={3}>
									{i + 1}. {record.name}
								</td>
								<td colSpan={3}>
									{i + 1}. {record.role}
								</td>
							</tr>
						))
					) : (
						<Fragment>
							<tr>
								<td rowSpan={2}>
									<strong>Recommending Committe</strong>
								</td>
								<td colSpan={3}>1.</td>
								<td colSpan={3}>1.</td>
							</tr>
							<tr>
								<td colSpan={3}>2.</td>
								<td colSpan={3}>2.</td>
							</tr>
						</Fragment>
					)}
					<tr>
						<td rowSpan={10}>
							<strong>
								Total Permohonan Fasilitas a.n Calon Debitur dan Group Usaha
							</strong>
							(Jika Ada)
						</td>
						<th className="bg">Uraian</th>
						<th className="bg">Jenis Plafon</th>
						<th className="bg">Exp Date</th>
						<th className="bg">Limit Existing</th>
						<th className="bg">Limit Pengajuan</th>
						<th className="bg">Total Limit</th>
					</tr>
					{data?.totalFacilityRequest?.debtorPlafons?.length ? (
						data?.totalFacilityRequest?.debtorPlafons.map((record, i) => (
							<tr key={i}>
								<td>
									{i === 0 && <strong>Fasilitas a.n calon debitur</strong>}
								</td>
								<td>{record.plafonType.value}</td>
								<td>
									{record.expiredDate &&
										dayjs(record.expiredDate).format('DD/MM/YYYY')}
								</td>
								<td>{formatCurrency(record.existingLimit)}</td>
								<td>{formatCurrency(record.applicationLimit)}</td>
								<td>{formatCurrency(record.totalLimit)}</td>
							</tr>
						))
					) : (
						<tr>
							<td>
								<strong>Fasilitas a.n calon debitur</strong>
							</td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
						</tr>
					)}
					<tr>
						<td className="bg">
							<strong>Total Debitur</strong>
						</td>
						<td className="bg"></td>
						<td className="bg"></td>
						<td className="bg"></td>
						<td className="bg"></td>
						<td className="bg"></td>
					</tr>
					{data?.totalFacilityRequest?.businessGroups?.length ? (
						data.totalFacilityRequest.businessGroups.map((record, i) => (
							<tr key={i}>
								<td>{record.name}</td>
								<td>{record.plafonType.value}</td>
								<td>
									{record.expiredDate &&
										dayjs(record.expiredDate).format('DD/MM/YYYY')}
								</td>
								<td>{formatCurrency(record.existingLimit)}</td>
								<td>{formatCurrency(record.applicationLimit)}</td>
								<td>{formatCurrency(record.totalLimit)}</td>
							</tr>
						))
					) : (
						<tr>
							<td>&nbsp;</td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
						</tr>
					)}
					<tr>
						<td className="bg">
							<strong>Total Group Usaha</strong>
						</td>
						<td className="bg"></td>
						<td className="bg"></td>
						<td className="bg">
							{formatCurrency(
								data?.totalFacilityRequest?.businessGroupTotal?.existingLimit ??
									0,
							)}
						</td>
						<td className="bg">
							{formatCurrency(
								data?.totalFacilityRequest?.businessGroupTotal
									?.applicationLimit ?? 0,
							)}
						</td>
						<td className="bg">
							{formatCurrency(
								data?.totalFacilityRequest?.businessGroupTotal?.totalLimit ?? 0,
							)}
						</td>
					</tr>
					<tr>
						<td colSpan={6} />
					</tr>
					<tr>
						<td style={{ background: '#e7e6e6' }}>
							<strong>Total Exposure JF</strong>
						</td>
						<td style={{ background: '#e7e6e6' }}></td>
						<td style={{ background: '#e7e6e6' }}></td>
						<td style={{ background: '#e7e6e6' }}>
							{formatCurrency(
								data?.totalFacilityRequest?.jfTotalExposure?.existingLimit ?? 0,
							)}
						</td>
						<td style={{ background: '#e7e6e6' }}>
							{formatCurrency(
								data?.totalFacilityRequest?.jfTotalExposure?.applicationLimit ??
									0,
							)}
						</td>
						<td style={{ background: '#e7e6e6' }}>
							{formatCurrency(
								data?.totalFacilityRequest?.jfTotalExposure?.totalLimit ?? 0,
							)}
						</td>
					</tr>
					<tr>
						<td style={{ background: '#e7e6e6' }}>
							<strong>Total Exposure NJF</strong>
						</td>
						<td style={{ background: '#e7e6e6' }}></td>
						<td style={{ background: '#e7e6e6' }}></td>
						<td style={{ background: '#e7e6e6' }}>
							{formatCurrency(
								data?.totalFacilityRequest?.njfTotalExposure?.existingLimit ??
									0,
							)}
						</td>
						<td style={{ background: '#e7e6e6' }}>
							{formatCurrency(
								data?.totalFacilityRequest?.njfTotalExposure
									?.applicationLimit ?? 0,
							)}
						</td>
						<td style={{ background: '#e7e6e6' }}>
							{formatCurrency(
								data?.totalFacilityRequest?.njfTotalExposure?.totalLimit ?? 0,
							)}
						</td>
					</tr>
					<tr>
						<td className="bg">
							<strong>Grand Total</strong>
						</td>
						<td className="bg"></td>
						<td className="bg"></td>
						<td className="bg">
							{formatCurrency(
								data?.totalFacilityRequest?.grandTotal?.existingLimit ?? 0,
							)}
						</td>
						<td className="bg">
							{formatCurrency(
								data?.totalFacilityRequest?.grandTotal?.applicationLimit ?? 0,
							)}
						</td>
						<td className="bg">
							{formatCurrency(
								data?.totalFacilityRequest?.grandTotal?.totalLimit ?? 0,
							)}
						</td>
					</tr>
					<tr>
						<td>
							<strong>Aging Date</strong>
						</td>
						<td colSpan={6}>
							{data?.agingDate && dayjs(data.agingDate).format('DD/MM/YYYY')}
						</td>
					</tr>
					<tr>
						<td
							colSpan={7}
							className="bg"
						>
							<strong>Data Bouwheer</strong>
						</td>
					</tr>
					<tr>
						<th>Bouwheer</th>
						<th>Kelas Industri</th>
						<th>Nama Pekerjaan</th>
						<th>Detail Kontrak</th>
						<th>Nilai Kontrak</th>
						<th>Tanggal Mulai</th>
						<th>Tanggal Berakhir</th>
					</tr>
					{data?.contracts.length ? (
						data.contracts.map((record, i) => (
							<tr key={i}>
								<td>{record.bouwheer?.name}</td>
								<td>{record.industrySector?.industryClass}</td>
								<td>{record.jobTitle}</td>
								<td>{record.contractDetail}</td>
								<td>{formatCurrency(record.contractValue)}</td>
								<td>
									{record.startDate &&
										dayjs(record.startDate).format('DD/MM/YYYY')}
								</td>
								<td>
									{record.endDate && dayjs(record.endDate).format('DD/MM/YYYY')}
								</td>
							</tr>
						))
					) : (
						<Fragment>
							<tr>
								<td>&nbsp;</td>
								<td></td>
								<td></td>
								<td></td>
								<td></td>
								<td></td>
								<td></td>
							</tr>
							<tr>
								<td>&nbsp;</td>
								<td></td>
								<td></td>
								<td></td>
								<td></td>
								<td></td>
								<td></td>
							</tr>
						</Fragment>
					)}
					<tr>
						<td>
							<strong>Informasi Tambahan</strong>
						</td>
						<td colSpan={6}>{data?.notes}</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const summaryConsentFragment = () => {
		const { data } = getNAPSummaryConsentQuery?.data ?? {};

		const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
			const img = e.target as HTMLImageElement;
			img.style.display = 'none';
			console.error('Failed to load image:', img.src);
		};

		return (
			<Fragment>
				{renderField(
					'1.',
					'SUMMARY COMPANY PERUSAHAAN',
					<Fragment>
						{data?.companyProfileSummaryFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.companyProfileSummaryFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.companyProfileSummary}</div>
					</Fragment>,
				)}
				{renderField(
					'2.',
					'HISTORI DI BMRI',
					<Fragment>
						{data?.bmriHistoryFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.bmriHistoryFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.bmriHistory}</div>
					</Fragment>,
				)}
				{renderField(
					'3.',
					'SUMMARY KEBUTUHAN DEBITUR SEBAGAI DASAR PEMBERIAN FASILITAS PEMBIAYAAN',
					<Fragment>
						{data?.financingRequirementSummaryFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.financingRequirementSummaryFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.financingRequirementSummary}</div>
					</Fragment>,
				)}
				{renderField(
					'4.',
					'ANALISA NERACA',
					<Fragment>
						{data?.balanceSheetAnalysisFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.balanceSheetAnalysisFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.balanceSheetAnalysis}</div>
					</Fragment>,
				)}
				{renderField(
					'5.',
					'ANALISA LABA RUGI',
					<Fragment>
						{data?.profitLossAnalysisFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.profitLossAnalysisFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.profitLossAnalysis}</div>
					</Fragment>,
				)}
				{renderField(
					'6.',
					'ANALISA CASHFLOW',
					<Fragment>
						{data?.cashFlowAnalysisFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.cashFlowAnalysisFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.cashFlowAnalysis}</div>
					</Fragment>,
				)}
				{renderField(
					'7.',
					'ANALISA RASIO KEUANGAN',
					<Fragment>
						{data?.financialRatioAnalysisFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.financialRatioAnalysisFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.financialRatioAnalysis}</div>
					</Fragment>,
				)}
				{renderField(
					'8.',
					'ANALISA SLIK',
					<Fragment>
						{data?.slikAnalysisFile?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={data?.slikAnalysisFile?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>{data?.slikAnalysis}</div>
					</Fragment>,
				)}
				{renderField('9.', 'INFORMASI TAMBAHAN', data?.notes)}
				{renderField(
					'10.',
					'EVIDENCE RATING CUSTOMER',
					<Fragment>
						{complianceData?.customerRatingEvidenceDocument?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={complianceData?.customerRatingEvidenceDocument?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>
							<strong>Rating:</strong> {complianceData?.customerRating?.value}
						</div>
						<div>
							<strong>Informasi:</strong>{' '}
							{complianceData?.customerRatingInformation}
						</div>
					</Fragment>,
				)}
				{renderField(
					'11.',
					'EVIDENCE RATING BOUWHEER',
					<Fragment>
						{complianceData?.bouwheerRatingEvidenceDocument?.url && (
							<img
								style={{ display: 'block', width: '75%', height: 'auto' }}
								src={complianceData?.bouwheerRatingEvidenceDocument?.url}
								loading="eager"
								onError={handleImageError}
								crossOrigin="anonymous"
							/>
						)}
						<div>
							<strong>Rating:</strong> {complianceData?.bouwheerRating?.value}
						</div>
						<div>
							<strong>Informasi:</strong>{' '}
							{complianceData?.bouwheerRatingInformation}
						</div>
					</Fragment>,
				)}
			</Fragment>
		);
	};

	const analysisFragment = () => {
		const { data: eddData } = getNAPEDDPEPAnalysisQuery?.data ?? {};

		const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
			const img = e.target as HTMLImageElement;
			img.style.display = 'none';
			console.error('Failed to load image:', img.src);
		};

		return (
			<Fragment>
				<table className="cell-border">
					<thead>
						<tr>
							<th
								className="bg"
								colSpan={7}
							>
								<strong>ANALISA PRODUKSI DAN TEKNIS</strong>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>IV.</strong>
								</center>
							</td>
							<td>
								<strong>Proses Produksi (apabila ada)</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.technicalCategory?.productionProcess}
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>V.</strong>
								</center>
							</td>
							<td>
								<strong>Realisasi dan Proyeksi Produksi</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.technicalCategory
										?.productionRealizationAndProjectionDocument?.url && (
										<img
											src={
												analysisData.technicalCategory
													.productionRealizationAndProjectionDocument.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									{
										analysisData?.technicalCategory
											?.productionRealizationAndProjectionAdditionalInfo
									}
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>VI.</strong>
								</center>
							</td>
							<td>
								<strong>Utilisasi Kebutuhan dan Analisa Produksi</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.technicalCategory
										?.needsAndAnalysisProductionUtilizationDocument?.url && (
										<img
											src={
												analysisData.technicalCategory
													.needsAndAnalysisProductionUtilizationDocument.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									{
										analysisData?.technicalCategory
											?.needsAndAnalysisProductionUtilizationAdditionalInfo
									}
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>VII.</strong>
								</center>
							</td>
							<td>
								<strong>Informasi Tambahan</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.technicalCategory?.notes}
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<table style={{ marginTop: 20 }}>
					<thead>
						<tr>
							<th
								className="bg"
								colSpan={7}
							>
								<strong>ASPEK KEUANGAN</strong>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>I.</strong>
								</center>
							</td>
							<td>
								<strong>Informasi KAP</strong>
								<div style={{ minHeight: 60 }}>
									<table className="cell-small cell-border">
										<thead>
											<tr>
												<th className="bg">Tahun</th>
												<th className="bg">KAP</th>
												<th className="bg">Opini</th>
												<th className="bg">Keterangan</th>
											</tr>
										</thead>
										<tbody>
											{analysisData?.financialCategory?.kapInformations
												?.length ? (
												analysisData.financialCategory.kapInformations.map(
													(record, i) => (
														<tr key={i}>
															<td>{record.year}</td>
															<td>{record.kap}</td>
															<td>{record.opinion}</td>
															<td>{record.description}</td>
														</tr>
													),
												)
											) : (
												<tr>
													<td>&nbsp;</td>
													<td></td>
													<td></td>
													<td></td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>II.</strong>
								</center>
							</td>
							<td>
								<strong>Neraca</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory?.balanceSheetDocument
										?.url && (
										<img
											src={
												analysisData.financialCategory.balanceSheetDocument.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									<strong>Hasil Analisa Neraca:</strong>
									<div>
										{
											analysisData?.financialCategory
												?.balanceSheetAnalysisResult
										}
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>III.</strong>
								</center>
							</td>
							<td>
								<strong>Laba Rugi</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory?.profitAndLossDocument
										?.url && (
										<img
											src={
												analysisData.financialCategory.profitAndLossDocument.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									<strong>Hasil Analisa Laba Rugi:</strong>
									<div>
										{
											analysisData?.financialCategory
												?.profitAndLossAnalysisResult
										}
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>IV.</strong>
								</center>
							</td>
							<td>
								<strong>Rasio Keuangan</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory?.financialRatioDocument
										?.url && (
										<img
											src={
												analysisData.financialCategory.financialRatioDocument
													.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									<strong>Hasil Analisa Rasio Keuangan:</strong>
									<div>
										{
											analysisData?.financialCategory
												?.financialRatiosAnalysisResult
										}
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>V.</strong>
								</center>
							</td>
							<td>
								<strong>Mutasi Rekening</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory
										?.bankStatementAnalysisDocument?.url && (
										<img
											src={
												analysisData.financialCategory
													.bankStatementAnalysisDocument.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									<strong>Informasi Tambahan Mutasi Rekening:</strong>
									<div>
										{
											analysisData?.financialCategory
												?.bankStatementAnalysisAdditionalInfo
										}
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>VI.</strong>
								</center>
							</td>
							<td>
								<strong>Analisa Pembiayaan</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory?.repaymentCapacityDocument
										?.url && (
										<img
											src={
												analysisData.financialCategory.repaymentCapacityDocument
													.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									<strong>Informasi Tambahan Analisa Pembiayaan:</strong>
									<div>
										{
											analysisData?.financialCategory
												?.repaymentCapacityAdditionalInfo
										}
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>VII.</strong>
								</center>
							</td>
							<td>
								<strong>Proyeksi Cashflow</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory?.cashFlowProjectionDocument
										?.url && (
										<img
											src={
												analysisData.financialCategory
													.cashFlowProjectionDocument.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									<strong>Informasi Tambahan Proyeksi Cashflow:</strong>
									<div>
										{
											analysisData?.financialCategory
												?.cashFlowProjectionAdditionalInfo
										}
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>VIII.</strong>
								</center>
							</td>
							<td>
								<strong>Evaluasi Guarantor</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory?.guarantorLetterDocument
										?.url && (
										<img
											src={
												analysisData.financialCategory.guarantorLetterDocument
													.url
											}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
									<br />
									<strong>Informasi Tambahan Evaluasi Guarantor:</strong>
									<div>
										{
											analysisData?.financialCategory
												?.guarantorEvaluationAdditionalInfo
										}
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td style={{ width: 60 }}>
								<center>
									<strong>IX.</strong>
								</center>
							</td>
							<td>
								<strong>Informasi Tambahan Aspek Keuangan</strong>
								<div style={{ minHeight: 60 }}>
									{analysisData?.financialCategory?.notes}
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<table style={{ marginTop: 20 }}>
					<tbody className="cell-border">
						<tr>
							<td
								className="bg"
								colSpan={2}
							>
								<strong>
									Analisa EDD (Enhanced Due Dilligence) High Risk & Politically
									Exposed Person (PEP)
								</strong>
							</td>
						</tr>
						<tr>
							<td style={{ width: '50%' }}>Hasil Analisa EDD & PEP</td>
							<td style={{ width: '50%' }}>{eddData?.analysisResult}</td>
						</tr>
					</tbody>
				</table>
			</Fragment>
		);
	};

	const riskFragment = () => {
		const { data } = getNAPRiskMitigationQuery?.data ?? {};

		return renderField(
			'a',
			'ASPEK RISIKO',
			<table className="cell-border">
				<tbody>
					<tr>
						<td>
							<strong>Risiko :</strong>
							<ul>
								{data?.riskAndMitigations?.map((record, i) => (
									<li key={i}>{record.risk}</li>
								))}
							</ul>
						</td>
						<td>
							<strong>Mitigasi :</strong>
							<ul>
								{data?.riskAndMitigations?.map((record, i) => (
									<li key={i}>{record.mitigation}</li>
								))}
							</ul>
						</td>
					</tr>
				</tbody>
			</table>,
		);
	};

	const recommendationFragment = () => {
		const { data } = getNAPRecommendationQuery?.data ?? {};

		const renderTerm = (title: string, value?: string) => {
			return (
				<Row wrap={false}>
					<Col
						flex="none"
						style={{ width: '50%' }}
					>
						{title}
					</Col>
					<Col
						flex="none"
						style={{ width: 10 }}
					>
						:
					</Col>
					<Col flex="auto">{value}</Col>
				</Row>
			);
		};

		return renderField(
			'b',
			'REKOMENDASI',
			<div>
				<strong>
					Rekomendasi / <s>Tidak Direkomendasikan</s> (skema)
				</strong>
				<table className="cell-border">
					<thead>
						<tr>
							<th
								className="bg"
								colSpan={2}
							>
								<strong>Rekomendasi / Persyaratan Pencairan Pembiayaan:</strong>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<strong>1. Ketentuan :</strong>
								<div
									style={{
										marginLeft: 20,
										marginBottom: 20,
									}}
								>
									{renderTerm(`1) Limit BPP`, formatCurrency(data?.bppLimit))}
									{renderTerm(`2) Jangka Waktu Plafon`, data?.plafonPeriod)}
									{renderTerm(
										`3) Jenis Pembiayaan`,
										data?.financingTypes?.map((d) => d.value).join(', '),
									)}
									{renderTerm(
										`4) Sifat Limit Pembiayaan`,
										data?.financingLimitType?.value,
									)}
									{renderTerm(
										`5) Objek Pembiayaan`,
										data?.financingObjects?.map((d) => d.value).join(', '),
									)}
									{renderTerm(
										`6) Down Payment`,
										data?.downPayment && `${data.downPayment}%`,
									)}
									{renderTerm(
										`7) Suku Bunga Efektif`,
										data?.effectiveInterestRate &&
											`${data.effectiveInterestRate}%`,
									)}
									{renderTerm(
										`8) Tenor`,
										data?.tenor ? `${data.tenor} Bulan` : undefined,
									)}
									{renderTerm(`9) Biaya Administrasi`, data?.administrationFee)}
									{renderTerm(
										`10) Provisi`,
										data?.provisionFee && `${data.provisionFee}%`,
									)}
									{renderTerm(`11) Servicing Fee`, data?.servicingFee)}
									{renderTerm(`12) Fidusia`, data?.fiduciaryFee)}
									{renderTerm(`13) Asuransi`, data?.insurance?.value)}
									{renderTerm(
										`14) Way of Payment (WOP)`,
										data?.paymentMethod?.value,
									)}
									{renderTerm(
										`15) Pengajuan Kondisi Khusus`,
										data?.specialCondition,
									)}
								</div>

								<strong>2. Ketentuan & Syarat Kredit :</strong>
								<table className="cell-small cell-border">
									<thead>
										<tr>
											<td className="bg">
												<strong>Ketentuan & Syarat</strong>
											</td>
											<td className="bg">
												<strong>Kategori</strong>
											</td>
											<td className="bg">
												<strong>Priority</strong>
											</td>
										</tr>
									</thead>
									<tbody>
										{data?.creditTermsAndConditions?.length ? (
											data.creditTermsAndConditions.map((record, i) => (
												<tr key={i}>
													<td>{record.termAndCondition}</td>
													<td>{record.category?.value}</td>
													<td>{record.priority?.value}</td>
												</tr>
											))
										) : (
											<Fragment>
												<tr>
													<td>&nbsp;</td>
													<td></td>
													<td></td>
												</tr>
												<tr>
													<td>&nbsp;</td>
													<td></td>
													<td></td>
												</tr>
											</Fragment>
										)}
									</tbody>
								</table>
								<div style={{ height: 500 }} />
								<div>Fleet Credit Analyst</div>
								<div>Tanggal: {dayjs().format('DD MMMM YYYY')}</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>,
		);
	};

	const signatureFragment = () => {
		return (
			<Fragment>
				<table className="cell-border">
					<thead>
						<tr>
							<th className="bg">PEJABAT</th>
							<th className="bg">CATATAN / PERTIMBANGAN</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<div>Tanggal masuk :</div>
								<div style={{ height: 200 }}>Tanggal keluar :</div>
								<div>Corp. Fleet 1 Business Dept Head</div>
							</td>
							<td></td>
						</tr>
						<tr>
							<td>
								<div>Tanggal masuk :</div>
								<div style={{ height: 200 }}>Tanggal keluar :</div>
								<div>Corp. Credit Dept Head</div>
							</td>
							<td></td>
						</tr>
					</tbody>
				</table>
				<div style={{ height: 200 }} />
				<div>
					<strong>LEMBAR DISPOSISI</strong>
				</div>
				<table className="cell-border">
					<thead>
						<tr>
							<th
								className="bg"
								rowSpan={2}
							>
								PEJABAT
							</th>
							<th
								className="bg"
								colSpan={2}
							>
								KEPUTUSAN
							</th>
							<th
								className="bg"
								rowSpan={2}
							>
								CATATAN / PERTIMBANGAN
							</th>
						</tr>
						<tr>
							<th className="bg">SETUJU</th>
							<th className="bg">TOLAK</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<div>Tanggal masuk :</div>
								<div style={{ height: 200 }}>Tanggal keluar :</div>
								<div>Corporate Fleet 1 Division Head</div>
							</td>
							<td></td>
							<td></td>
							<td></td>
						</tr>
						<tr>
							<td>
								<div>Tanggal masuk :</div>
								<div style={{ height: 200 }}>Tanggal keluar :</div>
								<div>Executive Credit Officer</div>
							</td>
							<td></td>
							<td></td>
							<td></td>
						</tr>
					</tbody>
				</table>
			</Fragment>
		);
	};

	return (
		<div
			id="download-nap"
			className="download-nap"
			style={{ width: '100%', maxWidth: '1600px' }}
		>
			{header()}
			{generalInfoFragment()}
			{summaryConsentFragment()}
			{analysisFragment()}
			{riskFragment()}
			{recommendationFragment()}
			{signatureFragment()}
		</div>
	);
};

export default NAPDownloadModalContentJF;
